import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { MenuService } from '../services/menu.service';

// Enforces menu customization at the route level: a menu hidden by the org's
// group config must not be reachable by typing its URL. When the target route
// belongs to a disabled group we send the user to their first visible menu (or
// the no-access page when they have none) instead of loading the hidden page.
@Injectable({
  providedIn: 'root',
})
export class MenuGroupGuard implements CanActivate, CanActivateChild {
  constructor(
    private menuService: MenuService,
    private router: Router,
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.check(state);
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.check(state);
  }

  private check(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.menuService.canAccessRoute(state.url).pipe(
      switchMap((allowed) => {
        if (allowed) {
          return of(true);
        }
        // Blocked: redirect to the first menu the user can actually see. That
        // target is always group-allowed, so this guard won't reject it again.
        return this.menuService.getFirstMenuLink().pipe(
          map((link) => this.router.parseUrl(link)),
        );
      }),
      catchError(() => of(true)),
    );
  }
}
